const CART_ROOT_SELECTOR = '#candyrack-slider-cart';
const OFFER_ITEM_SELECTOR = '.candyrack-slider-cart-offer-item-wrapper';
const ORIGINAL_SELECT_SELECTOR = '.candyrack-slider-cart-offer-item-variant-select select';
const PRODUCT_LINK_SELECTOR = '.candyrack-slider-cart-offer-item-header';
const PRODUCT_IMAGE_SELECTOR = '.candyrack-slider-cart-offer-item-image';
const HEADING_SELECTOR = '.candyrack-slider-cart-offers-title';
const ADD_BUTTON_SELECTOR = '.candyrack-slider-cart-offer-item-add-button';
const CART_CONTENT_SELECTOR = '.candyrack-slider-cart-content-wrapper';
const CART_TITLE_SELECTOR = '.candyrack-slider-cart-header-title';
const CART_CONFIG_SELECTOR = '#bape-candyrack-cart-config';
const FREE_SHIPPING_SELECTOR = '.bape-candy-free-shipping';
const PRODUCT_CACHE = new Map();
const OFFER_BINDINGS = new WeakMap();

let controlId = 0;
let enhancementScheduled = false;
let cartSummaryUpdatePromise = null;
let cartAccessibilityController;

function enhanceCartAccessibility(root) {
  const close = root.querySelector('.candyrack-slider-cart-close-button');
  if (close) {
    close.setAttribute('aria-label', 'Close cart');
    close.setAttribute('type', 'button');
  }
  if (cartAccessibilityController) {
    cartAccessibilityController();
    return;
  }
  let dialog;
  let wasOpen = false;
  let opener = null;
  let pendingOpener = null;
  const background = new Map();
  const visible = element => element instanceof HTMLElement && element.isConnected
    && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden'
    && getComputedStyle(element).display !== 'none';
  const isTopDialog = () => visible(dialog)
    && !document.querySelector('sdg-modal[open],quick-add-dialog dialog[open],dialog:modal');
  const focusables = () => [...dialog.querySelectorAll('a[href],button,input,select,textarea,[tabindex]')]
    .filter(element => element.tabIndex >= 0 && !element.matches(':disabled') && !element.closest('[inert]') && visible(element));
  const canRestoreFocus = element => element instanceof HTMLElement && element.isConnected
    && element !== document.body && element !== document.documentElement
    && visible(element) && !element.matches(':disabled')
    && !element.closest('dialog:not([open]),sdg-modal:not([open])');
  const releaseBackground = () => {
    for (const [element, inert] of background) element.inert = inert;
    background.clear();
  };
  const containBackground = () => {
    let branch = dialog;
    while (branch?.parentElement && branch !== document.body) {
      for (const sibling of branch.parentElement.children) {
        if (sibling === branch || !(sibling instanceof HTMLElement) || background.has(sibling)) continue;
        background.set(sibling, sibling.inert);
        sibling.inert = true;
      }
      branch = branch.parentElement;
    }
  };
  const focusCart = () => {
    const target = dialog.querySelector('.candyrack-slider-cart-close-button') || focusables()[0] || dialog;
    target.focus({ preventScroll: true });
  };
  // Save the initiating control before the quick shop closes and its form loses focus.
  document.addEventListener('click', event => {
    if (wasOpen || !(event.target instanceof Element)) return;
    const control = event.target.closest('button[name="add"],button[data-testid="cart-drawer-trigger"],.sticky-add-to-cart__button');
    if (!control) return;
    pendingOpener = control.closest('sdg-modal')?._prevActive
      || control.closest('quick-add-dialog')?.returnFocusTarget || control;
    // The app's icon selector can miss keyboard clicks on the containing button.
    // Use its public entry point so the hidden theme modal never opens as well.
    if (control.matches('[data-testid="cart-drawer-trigger"]')
      && typeof window.CandyRackSliderCart?.openCart === 'function') {
      event.preventDefault();
      event.stopImmediatePropagation();
      window.CandyRackSliderCart.openCart();
    }
  }, true);
  const observer = new MutationObserver(() => update());
  const update = () => {
    const nextDialog = document.querySelector('#candyrack-slider-cart-wrapper');
    if (dialog !== nextDialog) {
      observer.disconnect();
      releaseBackground();
      dialog = nextDialog;
      if (dialog) observer.observe(dialog, { attributes: true, attributeFilter: ['class', 'style', 'hidden'], childList: true, subtree: true });
    }
    const open = visible(dialog);
    if (open && !wasOpen) {
      const active = document.activeElement;
      opener = pendingOpener || active?.closest('sdg-modal')?._prevActive || active;
      pendingOpener = null;
    } else if (!open && wasOpen) {
      releaseBackground();
      const target = canRestoreFocus(opener)
        ? opener : document.querySelector('.header-actions__cart-icon')?.closest('button');
      target?.focus({ preventScroll: true });
    }
    wasOpen = open;
    if (open && isTopDialog()) {
      containBackground();
      if (!dialog.contains(document.activeElement)) focusCart();
    } else {
      releaseBackground();
    }
  };
  document.addEventListener('transitionend', update);
  document.addEventListener('focusin', event => {
    if (isTopDialog() && !dialog.contains(event.target)) focusCart();
  }, true);
  document.addEventListener('keydown', event => {
    if (!isTopDialog()) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      dialog.querySelector('.candyrack-slider-cart-close-button')?.click();
    } else if (event.key === 'Tab') {
      const items = focusables();
      const first = items[0];
      const last = items.at(-1);
      if (!first || !dialog.contains(document.activeElement) || document.activeElement === dialog
        || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus({ preventScroll: true });
      }
    }
  }, true);
  cartAccessibilityController = update;
  update();
}

function normalizeCandyRackRootIds() {
  const roots = document.querySelectorAll(CART_ROOT_SELECTOR);
  let nestedRootIndex = 0;

  for (const root of roots) {
    const parentRoot = root.parentElement?.closest(CART_ROOT_SELECTOR);
    if (!parentRoot) continue;

    nestedRootIndex += 1;
    root.id = `candyrack-slider-cart-content-${nestedRootIndex}`;
  }
}

function getCartConfig() {
  const configElement = document.querySelector(CART_CONFIG_SELECTOR);

  if (!(configElement instanceof HTMLScriptElement)) return {};

  try {
    return JSON.parse(configElement.textContent || '{}');
  } catch {
    return {};
  }
}

const CART_CONFIG = getCartConfig();

/**
 * @param {number} cents
 * @param {string} currency
 */
function formatMoney(cents, currency) {
  const hasPartialUnit = cents % 100 !== 0;

  return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: hasPartialUnit ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/**
 * @param {HTMLElement} root
 * @param {Record<string, any>} cart
 */
function renderCartSummaryEnhancements(root, cart) {
  if (!root.isConnected) return;

  for (const count of document.querySelectorAll('[ref="cartBubbleAccessibleCount"]')) {
    const value = String(cart.item_count || 0);
    if (count.textContent !== value) count.textContent = value;
  }

  const cartTitle = root.querySelector(CART_TITLE_SELECTOR);
  const configuredTitle = String(CART_CONFIG.cartTitle || 'CART').toUpperCase();
  const titleText = `${configuredTitle} (${cart.item_count || 0})`;

  if (cartTitle && cartTitle.textContent?.trim() !== titleText) {
    cartTitle.textContent = titleText;
  }

  const threshold = Number(CART_CONFIG.freeShippingThreshold) || 0;
  const existingProgress = root.querySelector(FREE_SHIPPING_SELECTOR);

  if (threshold <= 0 || !cart.item_count) {
    existingProgress?.remove();
    return;
  }

  const content = root.querySelector(CART_CONTENT_SELECTOR);
  if (!(content instanceof HTMLElement)) return;

  let progress = existingProgress;

  if (!(progress instanceof HTMLElement)) {
    progress = document.createElement('div');
    progress.className = 'bape-candy-free-shipping';
    progress.innerHTML = `
      <p class="bape-candy-free-shipping-message" aria-live="polite"></p>
      <div class="bape-candy-free-shipping-track" role="progressbar" aria-valuemin="0" aria-valuemax="100">
        <span class="bape-candy-free-shipping-fill"></span>
      </div>
    `;
  }

  if (content.firstElementChild !== progress) content.prepend(progress);

  const remaining = Math.max(0, threshold - Number(cart.total_price || 0));
  const percent = Math.min(100, Math.max(0, (Number(cart.total_price || 0) / threshold) * 100));
  const metMessage = String(CART_CONFIG.freeShippingMetMessage || "CONGRATS! YOU'VE GOT FREE SHIPPING!");
  const unmetMessage = String(
    CART_CONFIG.freeShippingUnmetMessage || "YOU'RE [amount] AWAY FROM FREE SHIPPING!"
  );
  const message = remaining === 0
    ? metMessage
    : unmetMessage.replace('[amount]', formatMoney(remaining, cart.currency));
  const messageElement = progress.querySelector('.bape-candy-free-shipping-message');
  const track = progress.querySelector('.bape-candy-free-shipping-track');
  const fill = progress.querySelector('.bape-candy-free-shipping-fill');
  const roundedPercent = Math.round(percent);

  if (messageElement && messageElement.textContent !== message) messageElement.textContent = message;

  if (track) {
    track.setAttribute(
      'aria-label',
      String(CART_CONFIG.freeShippingProgressLabel || 'Free shipping progress')
    );
    track.setAttribute('aria-valuenow', String(roundedPercent));
    track.setAttribute('aria-valuetext', message);
  }

  if (fill instanceof HTMLElement) {
    const progressValue = `${percent}%`;
    if (fill.style.getPropertyValue('--bape-candy-free-shipping-progress') !== progressValue) {
      fill.style.setProperty('--bape-candy-free-shipping-progress', progressValue);
    }
  }
}

/**
 * @param {HTMLElement} root
 */
function updateCartSummaryEnhancements(root) {
  if (cartSummaryUpdatePromise) return;

  cartSummaryUpdatePromise = fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  })
    .then((response) => (response.ok ? response.json() : null))
    .then((cart) => {
      if (cart) renderCartSummaryEnhancements(root, cart);
    })
    .catch(() => null)
    .finally(() => {
      cartSummaryUpdatePromise = null;
    });
}

/**
 * @param {string} productPath
 * @returns {Promise<Record<string, any> | null>}
 */
async function getProduct(productPath) {
  const pathname = new URL(productPath, window.location.origin).pathname;

  if (!PRODUCT_CACHE.has(pathname)) {
    PRODUCT_CACHE.set(
      pathname,
      fetch(`${pathname}.js`)
        .then(response => response.ok ? response.json() : null)
        .catch(() => null)
    );
  }

  return PRODUCT_CACHE.get(pathname);
}

/**
 * @param {Array<Record<string, any>>} variants
 * @param {number} optionIndex
 * @returns {Array<string>}
 */
function getOptionValues(variants, optionIndex) {
  return [...new Set(variants.map((variant) => variant.options?.[optionIndex]).filter(Boolean))];
}

/**
 * @param {Record<string, any>} product
 * @returns {Array<string>}
 */
function getOptionNames(product) {
  return (product.options || []).map((option) =>
    typeof option === 'string' ? option : option?.name || ''
  );
}

/**
 * @param {HTMLSelectElement} originalSelect
 * @param {string | number} variantId
 */
function selectOriginalVariant(originalSelect, variantId) {
  originalSelect.value = String(variantId);
  originalSelect.dispatchEvent(new Event('input', { bubbles: true }));
  originalSelect.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * @param {HTMLElement} item
 * @param {HTMLSelectElement} originalSelect
 * @param {Record<string, any>} product
 */
function buildVariantControls(item, originalSelect, product) {
  const allowedVariantIds = new Set(Array.from(originalSelect.options, (option) => option.value));
  const variants = (product.variants || []).filter((variant) =>
    allowedVariantIds.has(String(variant.id))
  );
  const optionNames = getOptionNames(product);
  if (!variants.length || !optionNames.length) return;

  const instanceId = ++controlId;
  const controls = document.createElement('div');
  const originalSelectWrapper = originalSelect.closest('.candyrack-slider-cart-offer-item-variant-select');
  const selectedVariant = variants.find(variant => String(variant.id) === originalSelect.value) || variants[0];
  const selections = [...selectedVariant.options];
  const menus = [];
  let updatingOriginal = false;

  controls.className = 'bape-candy-variant-controls';
  controls.dataset.bapeCandyVariantControls = 'ready';

  const syncMenus = (changedIndex = -1) => {
    for (let index = 0; index < menus.length; index++) {
      const matching = variants.filter(variant => variant.options.every((value, position) =>
        position >= index || value === selections[position]
      ));
      const values = getOptionValues(matching, index);
      if (!values.includes(selections[index])) selections[index] = values[0] || '';
      if (index <= changedIndex) continue;
      menus[index].replaceChildren(...values.map(value => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value.toUpperCase();
        option.selected = value === selections[index];
        return option;
      }));
    }
  };

  const applySelection = () => {
    const matchingVariant = variants.find(variant =>
      variant.options.length === selections.length &&
      variant.options.every((value, index) => value === selections[index])
    );
    if (!matchingVariant) return;
    updatingOriginal = true;
    selectOriginalVariant(originalSelect, matchingVariant.id);
    updatingOriginal = false;
  };

  optionNames.forEach((name, index) => {
    const field = document.createElement('div');
    const label = document.createElement('label');
    const select = document.createElement('select');
    field.className = 'bape-candy-option';
    label.htmlFor = `bape-candy-option-${product.id}-${instanceId}-${index}`;
    label.textContent = name.toUpperCase();
    select.id = label.htmlFor;
    select.className = 'bape-candy-option-select';
    select.dataset.optionIndex = String(index);
    select.addEventListener('change', () => {
      selections[index] = select.value;
      syncMenus(index);
      applySelection();
    });
    menus.push(select);
    field.append(label, select);
    controls.append(field);
  });

  syncMenus();
  originalSelectWrapper?.insertAdjacentElement('afterend', controls);
  originalSelectWrapper?.setAttribute('data-bape-candy-hidden', 'true');

  originalSelect.addEventListener('change', () => {
    const variant = variants.find(candidate => String(candidate.id) === originalSelect.value);
    if (!variant) return;
    if (!updatingOriginal) {
      selections.splice(0, selections.length, ...variant.options);
      syncMenus();
    }
    const productImage = item.querySelector(PRODUCT_IMAGE_SELECTOR);
    if (productImage instanceof HTMLImageElement && variant.featured_image?.src) {
      productImage.src = variant.featured_image.src;
    }
  });
  return controls;
}

/**
 * @param {HTMLElement} item
 */
async function enhanceOfferItem(item) {
  const originalSelect = item.querySelector(ORIGINAL_SELECT_SELECTOR);
  const productLink = item.querySelector(PRODUCT_LINK_SELECTOR);
  if (!(originalSelect instanceof HTMLSelectElement) || !(productLink instanceof HTMLAnchorElement)) {
    return;
  }

  const signature = Array.from(originalSelect.options, option => option.value).join(',');
  const previous = OFFER_BINDINGS.get(item);
  if (previous?.select === originalSelect && previous.signature === signature &&
      (previous.loading || previous.controls?.isConnected)) return;
  previous?.controls?.remove();
  const binding = { select: originalSelect, signature, loading: true, controls: null };
  OFFER_BINDINGS.set(item, binding);

  item.dataset.bapeCandyEnhancement = 'loading';
  const product = await getProduct(productLink.href);

  if (OFFER_BINDINGS.get(item) !== binding) return;
  binding.loading = false;
  if (!item.isConnected || !product) {
    delete item.dataset.bapeCandyEnhancement;
    return;
  }

  binding.controls = buildVariantControls(item, originalSelect, product);
  item.dataset.bapeCandyEnhancement = 'ready';
}

function enhanceCandyRackCart() {
  normalizeCandyRackRootIds();

  const root = document.querySelector(CART_ROOT_SELECTOR);
  if (!(root instanceof HTMLElement)) return;

  enhanceCartAccessibility(root);

  updateCartSummaryEnhancements(root);

  const heading = root.querySelector(HEADING_SELECTOR);
  if (heading && heading.textContent !== 'YOU MAY ALSO LIKE') {
    heading.textContent = 'YOU MAY ALSO LIKE';
  }

  for (const button of root.querySelectorAll(ADD_BUTTON_SELECTOR)) {
    const buttonText = button.querySelector('span');
    if (buttonText && buttonText.textContent?.trim() !== 'ADD TO CART') {
      buttonText.textContent = 'ADD TO CART';
    } else if (!buttonText && button.textContent?.trim() !== 'ADD TO CART') {
      button.textContent = 'ADD TO CART';
    }
    button.setAttribute('aria-label', 'ADD TO CART');
  }

  for (const item of root.querySelectorAll(OFFER_ITEM_SELECTOR)) {
    if (item instanceof HTMLElement) enhanceOfferItem(item);
  }
}

function scheduleEnhancement() {
  if (enhancementScheduled) return;
  enhancementScheduled = true;

  queueMicrotask(() => {
    enhancementScheduled = false;
    enhanceCandyRackCart();
  });
}

const observer = new MutationObserver(scheduleEnhancement);
observer.observe(document.documentElement, { childList: true, subtree: true });

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleEnhancement, { once: true });
} else {
  scheduleEnhancement();
}
